import type { ReactElement, ReactNode } from "react";

import type { IconName } from "../../design-system/icons";
import styles from "./EmptyState.module.css";
import { Heading } from "../atoms/Heading";
import type { HeadingLevel } from "../atoms/Heading";
import { Icon } from "../atoms/Icon";

export interface EmptyStateProps {
  readonly title: string;
  readonly body: string;
  /** An optional way out, usually the action that would create the first item. */
  readonly action?: ReactNode;
  /**
   * The title's heading level: one under the heading of the region it sits in. `2` (a page's
   * section) by default; a state inside a nested section passes its own depth.
   */
  readonly headingLevel?: HeadingLevel;
  /** The mockup's `.state-ic` glyph above the title; decorative (the glyph hides itself). */
  readonly icon?: IconName | undefined;
}

/**
 * What a container renders for a successful response with nothing in it (the mockup's `.state`).
 *
 * The title is a real heading (`<h2>` unless the container nests it deeper), not bold text: an empty region is a landmark
 * somebody navigating by heading needs to find, and bold text is invisible to
 * that. "Empty" is also never an error — reaching this component means the
 * request worked.
 */
export function EmptyState({ title, body, action, headingLevel = 2, icon }: EmptyStateProps): ReactElement {
  return (
    <div className={styles.state}>
      {icon === undefined ? null : (
        <span className={styles.icon}>
          <Icon name={icon} size="lg" />
        </span>
      )}
      <Heading level={headingLevel} className={styles.title}>
        {title}
      </Heading>
      <p className={styles.body}>{body}</p>
      {action === undefined ? null : <div className={styles.action}>{action}</div>}
    </div>
  );
}
